import { ReactNode, useState } from "react";

export function UserMessage({ content, time }: { content: string; time?: string }) {
  return (
    <article className="sa-chat-message">
      <div className="sa-chat-message__meta">
        <div className="sa-label" style={{ color: "var(--sa-accent)" }}>
          Inquiry
        </div>
        {time && (
          <time
            style={{
              fontFamily: "var(--sa-mono)",
              fontSize: 11,
              color: "var(--sa-muted-fg)",
            }}
          >
            {time}
          </time>
        )}
      </div>
      <div
        className="sa-chat-message__user-body"
        style={{
          fontFamily: "var(--sa-serif)",
          fontSize: 22,
          lineHeight: 1.4,
          fontStyle: "italic",
          color: "var(--sa-fg)",
          maxWidth: 680,
          letterSpacing: "-0.005em",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {content}
      </div>
    </article>
  );
}

type AssistantProps = {
  answer?: string;
  sources?: string[];
  latencyMs?: number;
  time?: string;
  thinking?: boolean;
};

export function AssistantMessage({
  answer,
  sources,
  latencyMs,
  time,
  thinking,
}: AssistantProps) {
  const visibleSources = (sources ?? []).filter((source) => !isFaqSource(source));

  if (thinking) {
    return (
      <article className="sa-chat-message">
        <div className="sa-label" style={{ marginBottom: 18, color: "var(--sa-muted-fg)" }}>
          Instrument - thinking
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", height: 20 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--sa-muted-fg)",
                animation: `sa-pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      </article>
    );
  }

  return (
    <article className="sa-chat-message">
      <div className="sa-chat-message__meta" style={{ marginBottom: 24 }}>
        <div className="sa-label">Instrument</div>
        {time && (
          <time
            style={{
              fontFamily: "var(--sa-mono)",
              fontSize: 11,
              color: "var(--sa-muted-fg)",
            }}
          >
            {time}
          </time>
        )}
      </div>
      <div
        className="sa-chat-message__assistant-body"
        style={{
          fontFamily: "var(--sa-serif)",
          fontSize: 17,
          lineHeight: 1.75,
          color: "var(--sa-fg)",
          maxWidth: 680,
        }}
      >
        {renderAnswer(answer ?? "")}
      </div>
      {visibleSources.length > 0 && (
        <SourcesAccordion sources={visibleSources} latencyMs={latencyMs} />
      )}
    </article>
  );
}

function renderAnswer(text: string): ReactNode {
  if (!text) return null;
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs.map((paragraph, i) => (
    <p key={i} style={{ margin: i === paragraphs.length - 1 ? 0 : "0 0 20px" }}>
      {renderWithRefs(paragraph)}
    </p>
  ));
}

const REF_RE = /\(([\d:\u2013\-,\s]+)\)/g;

function renderWithRefs(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  let key = 0;

  for (const match of text.matchAll(REF_RE)) {
    const idx = match.index ?? 0;
    if (idx > last) parts.push(text.slice(last, idx));
    parts.push(
      <span
        key={key++}
        style={{
          fontFamily: "var(--sa-mono)",
          fontSize: 12,
          color: "var(--sa-accent)",
          background: "var(--sa-accent-soft)",
          padding: "1px 6px",
          borderRadius: 2,
          whiteSpace: "nowrap",
        }}
      >
        {match[1]}
      </span>,
    );
    last = idx + match[0].length;
  }

  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function SourcesAccordion({
  sources,
  latencyMs,
}: {
  sources: string[];
  latencyMs?: number;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        marginTop: 28,
        borderTop: "1px solid var(--sa-border)",
        paddingTop: 16,
      }}
    >
      <button
        className="sa-chat-sources__toggle"
        onClick={() => setOpen(!open)}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: "var(--sa-sans)",
          fontSize: 12,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--sa-muted-fg)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
        }}
      >
        <span className={`sa-chat-sources__caret${open ? " is-open" : ""}`}>▸</span>
        Sources - {sources.length}
        <span style={{ flex: 1 }} />
        {latencyMs !== undefined && (
          <span
            style={{
              fontFamily: "var(--sa-mono)",
              fontSize: 11,
              color: "var(--sa-muted-fg)",
            }}
          >
            {(latencyMs / 1000).toFixed(2)}s
          </span>
        )}
      </button>
      {open && (
        <ol
          style={{
            marginTop: 14,
            paddingLeft: 22,
            fontFamily: "var(--sa-mono)",
            fontSize: 12,
            lineHeight: 2,
            color: "var(--sa-fg)",
          }}
        >
          {sources.map((source, i) => (
            <li key={i}>
              <SourceLink source={source} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SourceLink({ source }: { source: string }) {
  const url = toUrl(source);
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title="Open source in a new window"
        style={{
          color: "var(--sa-accent)",
          textDecoration: "underline",
          textUnderlineOffset: 3,
          wordBreak: "break-all",
        }}
      >
        {source}
      </a>
    );
  }

  return <span style={{ wordBreak: "break-all" }}>{source}</span>;
}

function isFaqSource(source: string): boolean {
  return source.trim().toLowerCase().startsWith("faq:");
}

function toUrl(s: string): string | null {
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:" ? u.toString() : null;
  } catch {
    return null;
  }
}
